import { useCustomersStore } from '@/store/customers-store';
import { defineStore } from 'pinia';
import { AlertType, useUiStore } from '@/store/ui-store';
import { Login, User } from '@/model/user';
import { HARMONY_VENDOR_ID, L_PITTET_APP_ID } from '@/constants';
import jwtDecode from 'jwt-decode';
import userService from '@/services/user.service';
import i18n, { getAvailableLanguages, Lang, loadLanguage } from '@/config/i18n';
import * as Sentry from '@sentry/vue';
import { Role } from '@/model/role';
import { getMainCollections } from '@/store/common/utilities';
import { useVendorsStore } from '@/store/vendors-store';
import { useCalendarStore } from '@/store/calendar-store';
import { useEmployeesStore } from '@/store/employees-store';
import { useBookingsStore } from '@/store/bookings-store';
import cloneDeep from 'lodash-es/cloneDeep';
import { reactive } from 'vue';
import { Spaces } from '@/model/space';
import { DeviceSession } from '@/model/device-session';

const storeId = 'users';
const EXPIRED_SESSION_STATUS = 401;

let initialState = reactive({
  user: {
    fetching: false as boolean,
    failure: false as boolean,
    saving: false as boolean,
    entity: {} as User,
    loggingIn: false as boolean,
    token: null as string | null,
    validationErrors: {} as Record<string, never>,
    language: 'fr' as string,
    spaces: { vendors: [] } as Spaces,
    switchingSpace: false as boolean,
  },
  device: {
    id: null as string | null,
    sessions: [] as DeviceSession[],
    unlocking: false as boolean,
  },
});
export const usersInitialStateClone = cloneDeep(initialState);
if (localStorage.getItem(storeId)) {
  initialState = reactive({
    ...initialState,
    ...JSON.parse(localStorage.getItem(storeId)),
  });
  initialState.user.validationErrors = {};
  initialState.user.loggingIn = false;
  initialState.user.fetching = false;
  initialState.user.failure = false;
  initialState.user.switchingSpace = false;
  initialState.user.spaces ??= cloneDeep(usersInitialStateClone.user.spaces);
  initialState.device ??= cloneDeep(usersInitialStateClone.device);
  initialState.device.id ??= null;
  initialState.device.sessions = (initialState.device.sessions ?? []).filter(
    (session: DeviceSession) => session.secret !== undefined,
  );
  initialState.device.unlocking = false;
}

export const useUsersStore = defineStore(storeId, {
  state: () => {
    return {
      ...initialState,
    };
  },
  getters: {
    isLoggedIn(): boolean {
      return !!this.user.token;
    },
    loggedInUserName(): string | null {
      return this.user.token !== null ? jwtDecode(this.user.token).name : null;
    },
    loggedInUserRole(): Role | null {
      return this.user.token !== null
        ? parseInt(jwtDecode(this.user.token).role)
        : null;
    },
    loggedInUserNotValidatedEmail(): boolean {
      return this.user.token !== null
        ? !!jwtDecode(this.user.token).notValidated
        : true;
    },
    loggedInEmployeeId(): number | null {
      return this.user.token !== null && jwtDecode(this.user.token).employeeId
        ? jwtDecode(this.user.token).employeeId
        : null;
    },
    loggedInUserId(): number | null {
      return this.user.token !== null && jwtDecode(this.user.token).sub
        ? jwtDecode(this.user.token).sub
        : null;
    },
    loggedInVendorId(): number | null {
      return this.user.token !== null && jwtDecode(this.user.token).vendorId
        ? jwtDecode(this.user.token).vendorId
        : null;
    },
    isFromLPittet(): boolean {
      return this.user.token !== null && jwtDecode(this.user.token).appId
        ? jwtDecode(this.user.token).appId === L_PITTET_APP_ID
        : false;
    },
    isFromHarmony(): boolean {
      return this.user.token !== null && jwtDecode(this.user.token).vendorId
        ? jwtDecode(this.user.token).vendorId === HARMONY_VENDOR_ID
        : false;
    },
    isAdmin(): boolean {
      return (
        this.user.token !== null &&
        jwtDecode(this.user.token).role === Role.ADMIN
      );
    },
    isStaffOrMore(): boolean {
      return (
        this.user.token !== null &&
        jwtDecode(this.user.token).role >= Role.STAFF
      );
    },
    isStaff(): boolean {
      return (
        this.user.token !== null &&
        jwtDecode(this.user.token).role === Role.STAFF
      );
    },
    isCustomerOrMore(): boolean {
      return (
        this.user.token !== null &&
        jwtDecode(this.user.token).role >= Role.CUSTOMER
      );
    },
    isCustomer(): boolean {
      return (
        this.user.token !== null &&
        jwtDecode(this.user.token).role === Role.CUSTOMER
      );
    },
    isGuest(): boolean {
      return this.user.token === null;
    },
    hasSeveralSpaces(): boolean {
      return this.user.spaces.vendors.length > 0;
    },
    otherDeviceSessions(): DeviceSession[] {
      return this.device.sessions.filter(
        (session) => session.userId !== this.loggedInUserId,
      );
    },
    hasOtherDeviceSessions(): boolean {
      return this.otherDeviceSessions.length > 0;
    },
    canSwitchEmployee(): boolean {
      return this.isStaffOrMore && useEmployeesStore().entities.length > 1;
    },
    mustDefinePin(): boolean {
      return this.canSwitchEmployee && !this.user.entity.has_pin;
    },
    pinActionLabel(): string {
      return this.user.entity.has_pin
        ? i18n.global.t('deviceSessions.changeTitle')
        : i18n.global.t('deviceSessions.setupTitle');
    },
  },
  actions: {
    async getUser(userId: number) {
      const uiStore = useUiStore();
      try {
        this.user.fetching = true;
        const { user, spaces } = await userService.getById(userId);
        this.user.fetching = false;
        this.user.entity = user;
        this.user.spaces = spaces;
        if (user.id === this.loggedInUserId) {
          await this.rememberSession();
        }

        return user;
      } catch (exception) {
        this.user.failure = true;
        uiStore.alert(exception.message);
      }
    },
    async edit(user: Partial<User>) {
      const uiStore = useUiStore();
      try {
        this.user.failure = false;
        this.user.saving = true;
        this.user.validationErrors = {};
        const userEdited: User = await userService.edit(user);
        this.user.entity = Object.assign(this.user.entity, userEdited);
        this.user.saving = false;
        if (this.user.entity.id === this.loggedInUserId) {
          await this.rememberSession();
        }
        uiStore.alert(
          i18n.global.t('myAccountEdit.success'),
          AlertType.SUCCESS,
        );

        return true;
      } catch (exception) {
        this.user.failure = true;
        this.user.saving = false;
        if (exception.errors !== undefined) {
          this.user.validationErrors = exception.errors;
        } else {
          this.user.validationErrors = {};
        }
        uiStore.alertValidationError(exception);
        return false;
      }
    },
    async signUp(userData: Partial<User>) {
      const uiStore = useUiStore();
      try {
        this.user.saving = true;
        const messageAndToken = await userService.signUp(userData);
        this.user.saving = false;
        this.user.token = messageAndToken.token;
        uiStore.alert(messageAndToken.message, AlertType.SUCCESS);
        this.user.validationErrors = {};

        return true;
      } catch (exception) {
        this.user.saving = false;
        if (exception.errors !== undefined) {
          this.user.validationErrors = exception.errors;
        }
        uiStore.alertValidationError(exception);

        return false;
      }
    },
    async emailValidation(id: number | string, token: string) {
      const uiStore = useUiStore();
      try {
        const messageAndToken = await userService.validateEmail({ id, token });
        this.user.token = messageAndToken.token;
        uiStore.alert(messageAndToken.message, AlertType.SUCCESS, 15);
      } catch (exception) {
        uiStore.alert(exception.message);
      }
    },
    async login(user: Login, keepBooking = false) {
      const uiStore = useUiStore();
      const vendorsStore = useVendorsStore();
      this.user.loggingIn = true;
      this.user.failure = false;
      try {
        const { token, spaces } = await userService.login(user);
        this.resetVendorData(keepBooking);
        this.user.entity = {} as User;
        this.user.token = token;
        this.user.spaces = spaces;
        this.user.loggingIn = false;
        if (this.isStaffOrMore) {
          vendorsStore.getById(this.loggedInVendorId as number);
        }
        Sentry.setUser({ username: user.usernameOrEmail });
      } catch (exception) {
        this.user.loggingIn = false;
        this.user.token = null;
        this.user.failure = true;
        uiStore.alert(exception.message);
      }
    },
    refreshToken(token: string) {
      this.user.token = token;
    },
    async rememberSession() {
      if (!this.isStaffOrMore || !this.user.entity.has_pin) {
        return;
      }
      const known = this.device.sessions.find(
        (candidate) => candidate.userId === this.loggedInUserId,
      );
      if (known) {
        known.name = this.loggedInUserName as string;

        return;
      }
      try {
        const registered = await userService.registerDevice(this.device.id);
        this.device.id = registered.device_id;
        this.device.sessions.push({
          id: registered.id,
          userId: this.loggedInUserId as number,
          name: this.loggedInUserName as string,
          secret: registered.secret,
        });
      } catch (exception) {
        Sentry.captureException(exception);
      }
    },
    async releaseSession(userId: number | null) {
      const session = this.device.sessions.find(
        (candidate) => candidate.userId === userId,
      );
      if (!session) {
        return;
      }
      try {
        await userService.forgetDeviceSession(session.id, session.secret);
      } catch (exception) {
        if (exception.status !== EXPIRED_SESSION_STATUS) {
          Sentry.captureException(exception);
        }
      }
    },
    forgetSession(userId: number) {
      this.device.sessions = this.device.sessions.filter(
        (session) => session.userId !== userId,
      );
    },
    async setPin(pin: string): Promise<boolean> {
      const uiStore = useUiStore();
      try {
        this.user.saving = true;
        this.user.validationErrors = {};
        const { has_pin } = await userService.setPin(pin);
        this.user.entity.has_pin = has_pin;
        await this.rememberSession();
        uiStore.alert(i18n.global.t('deviceSessions.saved'), AlertType.SUCCESS);

        return true;
      } catch (exception) {
        this.user.validationErrors = exception.errors ?? {};
        uiStore.alertValidationError(exception);

        return false;
      } finally {
        this.user.saving = false;
      }
    },
    async unlockSession(userId: number, pin: string): Promise<boolean> {
      const uiStore = useUiStore();
      const session = this.device.sessions.find(
        (candidate) => candidate.userId === userId,
      );
      if (!session) {
        uiStore.alert(i18n.global.t('deviceSessions.expired'));

        return false;
      }
      this.device.unlocking = true;
      try {
        const token = await userService.unlockSession(
          session.id,
          session.secret,
          pin,
        );
        this.resetVendorData();
        this.user.entity = {} as User;
        this.user.spaces = cloneDeep(usersInitialStateClone.user.spaces);
        this.user.token = token;

        return true;
      } catch (exception) {
        if (exception.status === EXPIRED_SESSION_STATUS) {
          this.forgetSession(userId);
          uiStore.alert(i18n.global.t('deviceSessions.expired'));

          return false;
        }
        uiStore.alert(exception.message);

        return false;
      } finally {
        this.device.unlocking = false;
      }
    },
    async enterSpace(vendorId: number | null) {
      this.user.switchingSpace = true;
      try {
        const token = await userService.enterSpace(vendorId);
        this.resetVendorData();
        this.user.token = token;
      } finally {
        this.user.switchingSpace = false;
      }
    },
    resetVendorData(keepBooking = false) {
      useCalendarStore().reset();
      useCustomersStore().reset();
      useEmployeesStore().reset();
      if (!keepBooking) {
        useBookingsStore().$reset();
      }
      useVendorsStore().unsetCurrentEntity();
    },
    reset() {
      this.$state = Object.assign(
        {},
        this.$state,
        reactive(cloneDeep(usersInitialStateClone)),
      );
    },
    async logout() {
      const remaining = this.otherDeviceSessions;
      await this.releaseSession(this.loggedInUserId);
      this.keepDeviceSessions(remaining);
    },
    parkSession() {
      this.keepDeviceSessions(this.device.sessions);
    },
    keepDeviceSessions(sessions: DeviceSession[]) {
      const deviceId = this.device.id;
      this.clearDevice();
      this.device.id = deviceId;
      this.device.sessions = sessions;
      localStorage.setItem(storeId, JSON.stringify(this.$state));
    },
    async logoutDevice() {
      await Promise.all(
        this.device.sessions.map((session) =>
          this.releaseSession(session.userId),
        ),
      );
      this.clearDevice();
    },
    clearDevice() {
      this.reset();
      this.resetVendorData();
      localStorage.clear();
      Sentry.getCurrentScope().setUser(null);
    },
    async forgottenPassword(user: Partial<User>) {
      const uiStore = useUiStore();
      try {
        await userService.forgottenPassword(user);
        return true;
      } catch (exception) {
        uiStore.alert(exception.message);
        return false;
      }
    },
    async resetPassword(user: Partial<User>) {
      const uiStore = useUiStore();
      try {
        const data = await userService.resetPassword(user);
        this.forgetRevokedAccount(user.id);
        uiStore.alert(data.message, AlertType.SUCCESS);
        return true;
      } catch (exception) {
        uiStore.alert(exception.message);
        return false;
      }
    },
    forgetRevokedAccount(userId: number) {
      const remaining = this.device.sessions.filter(
        (session) => session.userId !== userId,
      );
      if (this.loggedInUserId !== userId) {
        this.device.sessions = remaining;

        return;
      }
      this.keepDeviceSessions(remaining);
    },
    async loadDataForStaffUser() {
      const vendorsStore = useVendorsStore();
      if (this.isStaffOrMore) {
        const employeesStore = useEmployeesStore();
        const usersStore = useUsersStore();
        const vendorsUnsubscribe = vendorsStore.$onAction(({ name, after }) => {
          if (name === 'findOne') {
            vendorsUnsubscribe();
            after(() => {
              const vendor = vendorsStore.getById(
                this.loggedInVendorId as number,
              );
              if (vendor) {
                vendorsStore.setCurrentEntity(vendor);
              }
            });
          }
        });
        const unsubscribe = employeesStore.$onAction(({ name, after }) => {
          if (name === 'findAll') {
            unsubscribe();
            after(() => {
              if (this.isStaff || !employeesStore.currentEntity?.id) {
                employeesStore.setCurrentEntityWithTimetable(
                  usersStore.loggedInEmployeeId,
                );
              }
            });
          }
        });
        await getMainCollections(this.loggedInVendorId as number, true);
      }
    },
    async setLanguage(language: Lang) {
      const uiStore = useUiStore();
      if (getAvailableLanguages().indexOf(language) >= 0) {
        await loadLanguage(language);

        this.user.language = language;
        if (this.isLoggedIn) {
          try {
            await userService.changeAccountLanguage(language);
          } catch (exception) {
            uiStore.alert(exception.message);
          }
        }
      }
    },
  },
});
